/* eslint-disable no-template-curly-in-string */
/**
  * @class melePlayer
  * @description
  *
  * # Purpose
  * Implements `<mele-player>` custom web element for playing streams from msysStreamer
  */

import meleElement from 'meleElement'
import msysListener from 'msysListener'
import { NAL } from '../meleActorH264/nal'

const MIN_GOOD_US = new Date('2020-01-01 00:00:00Z').valueOf()*1000

const PROPERTIES = _.extend({}, {
  listener_id: { type: 'string', description: 'desired listener_id for the streaming listener; if not given, automatic numeric ID will be assigned' },
  source: { type: 'string', description: 'source of streaming; empty string means our application, @ means peering, Bxxxxx: means remote machine', default: 'SERVER@msysStreamer' },
  autoplay: { type: 'boolean', description: 'start autoplay of data' },
  autopause: { type: 'boolean', description: 'auto pause when parent module looses focus', default: true },
  recording: { type: 'string', description: 'behavior of recording button', default: 'none', enum: ['none', 'disabled', 'enabled'] },
  mock: { type: 'boolean', description: 'indicates mockup is in progress', default: false },
  mock_filter: { type: 'string', description: 'name of streaming filter or JSON of filter for mock', default: 'mock' },
  mock_h264_shift: { type: 'integer', description: 'number of miliseconds to postpone H264 frames in mock mode; -1 means postpone corresponding to log_dt-event_dt of the frame; useful for simulating network delay', default: 0 },
  filter: { type: 'string', description: 'name of streaming filter or JSON of filter', default: 'player' },
  low: { type: 'boolean', description: 'if true, non-selected channels are retrieved in low quality', default: true },
  controls: { type: 'boolean', description: 'if true, playback controls will be displayed', default: false },
  marker: { type: 'string', description: 'behavior of marker', enum: ['moment', 'interval'] },
  markering: { type: 'boolean', description: 'indicates whether marking is in progress' },
  marker_window: { type: 'integer', description: 'number of seconds allowed for marker image preview window', default: 5, minimum: 0 },
  marker_q: { type: 'integer', description: 'quality of marker image preview: 2-63', default: 12, minimum: 2, maximum: 63 },
  marker_width: { type: 'integer', description: 'maximum desired width for marker preview images', default: 512 },
  marker_height: { type: 'integer', description: 'maximum desired height for marker preview images; if both width and height is specified, aspect ratio will be preserved', default: 288 },
  marker_before: { type: 'integer', description: 'time in microseconds to start marking before user started marking', default: 0 },
  marker_after: { type: 'integer', description: 'time in microseconds to end marking after user ended marking', default: 0 },
  marker_max: { type: 'integer', description: 'maximum time in microseconds for interval marker after which it is automatically turned off', default: 10_000_000 },
  markers_before: { type: 'integer', description: 'time in seconds to search for markers before current playtime for list of markers', default: 30 * 24 * 60 * 60 },
  markers_after: { type: 'integer', description: 'time in seconds to search for markers before current playtime for list of markers', default: 30 * 24 * 60 * 60 },
  delict: { type: 'boolean', description: 'if true, delict button and frame collection tray are available in historic playback', default: false },
  delict_max: { type: 'integer', description: 'maximum number of frames that can be collected for one delict', default: 8 },
  delict_channels: { type: 'array', items: { type: 'integer' }, description: 'channels on which delicts can be created; empty array means all channels are allowed', default: [] },
  timeline: { type: 'boolean', description: 'whether to show timeline control', default: false },
  search_window: { type: 'integer', description: 'maximum number of seconds forward/backward to search in fwd/rwdMark', default: 7 * 24 * 60 * 60 },
  search_rwd: { type: 'number', description: 'number of seconds before current ETS to end rwdMark to', default: 5 },
  search_fwd: { type: 'number', description: 'number of seconds after current ETS to start fwdMark from', default: 0.5 },
  state: { type: 'string', description: 'state of the element', enum: ['stopped', 'connecting', 'playing', 'paused'], default: 'stopped' },
  selected: { description: 'selected channel', type: 'integer' },
  verbose: { description: 'whether to show diagnostic messages; 1 shows errors and warnings, 2 informations, 3 debugs', type: 'integer' },
  devel: { description: 'if ture, display developer info in GUI', type: 'boolean' },
  start: { description: 'required start time in microseconds', type: 'integer' },
  live: { description: 'indicates live playback', type: 'boolean', readonly: true },
  bts: { description: 'beginning timestamp - event_dt time of first incoming streaming message in microseconds', type: 'integer', readonly: true },
  ets: { description: 'event timestamp - current playback time in microseconds', type: 'integer', readonly: true },
  mts: { description: 'last mmTime recived in microseconds', type: 'integer', readonly: true },
  pts: { description: 'presentation timestamp - current offset of ets since bts in microseconds', type: 'integer', readonly: true },
  bdt: { description: 'beginning date time', type: 'string', readonly: true },
  edt: { description: 'event date time', type: 'string', readonly: true },
  vts: { description: 'video timestamp - current video time in microseconds', type: 'integer', readonly: true },
  videosize: { description: 'video resolution: WIDTHxHEIGHT', type: 'string', readonly: true },
  rendersize: { description: 'video render size: WIDTHxHEIGHT', type: 'string', readonly: true },
  routing: { description: 'configuration of custom routing of messages to actors; JSON object with keys representing message action and value as list of mele-actor tags where the message will be forwarded to', type: 'string' },
  eventRewindMark: { type: 'function', description: 'Called when rewind mark button is clicked' },
  eventForwardMark: { type: 'function', description: 'Called when forward mark button is clicked' },
  eventDelictCreate: { type: 'function', description: 'Called when user confirms creation of delict from collected frames' },
  skiped_channels: { type: 'array', description: 'array of channel numbers which should not be rendered in ui controls', default: [] },
  screenshot_filename: {
    description: 'filename for download',
    type: 'string',
    // eslint-disable-next-line no-template-curly-in-string
    default: 'mosy_${appid}_${channel}_${yyyy}${mm}${dd}_${hh}${mi}${ss}.jpg',
  },
  screenshot_quality: {
    description: 'quality of picture in %',
    type: 'integer',
    default: 90,
  },
  mounts: { type: 'string', description: 'regexp for matching volume mounts for export' },
  mounts_exclude: { type: 'string', description: 'regexp for excluding volume mounts for export' },
  mounts_extra: { type: 'array', item: { type: 'string' }, description: 'additional extra mounts' },
  exports: {
    type: 'object',
    description: 'used to define how the data can be exported',
    properties: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['data', 'video'],
          description: 'type of export, either data or video',
        },
        filter: {
          type: 'array',
          description: 'list of filter rules as []',
        },
      },
      required: ['type', 'filter'],
    },
    default: {
      doppler: {
        type: 'data',
        filter: [['KEEP', 'action:mmDopplerHit']],
      },
      marked: {
        type: 'marked',
        filter: [
          ['KEEP', 'action:mmH264,mmGps'],
          ['SKIP', 'action:mmH264', '.channel!1'],
        ],
      },
      videos: {
        type: 'video',
        filter: [
          ['KEEP', 'action:mmH264,mmGps'],
          ['SKIP', 'action:mmH264', '.channel!1'],
        ],
      },
    },
  },
}, meleElement._properties)

export default class melePlayer extends meleElement {
  async init() {
    this.setProperty('recording', this.recording || 'none', null, true)
    this.elChannels = this.appendChildren({ tag: 'channels' })
    this.channels = {}
    this.listener_idChanged(this.listener_id)
    if(this.controls) {
      this.setAttribute('controls', '')
      this.controlsChanged(this.controls)
    }
    super.init()
  } // init

  disconnectedCallback() {
    if(this.isParkedDisconnect && this.isParkedDisconnect()) return
    if(this.listener) {
      this.pause()
      this.listener.mmExit(null, { delete: true })
      delete this.listener
    }
  } // disconnectedCallback

  async playIfAuto(start) {
    if(this.autoplay && (!this.autopause || this.window_active)) {
      return this.play(start)
    }
  }

  async play(start) {
    console.log2('play', { start })
    if(this.active_source && this.listener?.playing && this.active_source!==this.source) {
      // stop previous playback to another source
      // not needed if source does not change as the source itself should replace old playback with new one
      this.listener.playing = false
      try{
        await this.listener.request(this.active_source, 'mmStop', {}, { resolve: 'mmSuccess', timeout: 2000 })
      }catch(e) {
        console.error('failed to stop playback: '+e)
      }
    }

    if(this.listener) {
      this.listener.playing = false
    }

    // if start time
    start = parseInt(start)
    if(isNaN(start)) {
      // if start time not provided, use "start" attribute or live playback if "start" attribute is empty
      start = parseInt(this.start) || 0
    }
    this._play(start)
    this.forwardMethod('play', start)
  } // play

  resume() {
    // when resuming, start live playback if "start" attribute is empty, or last played time (if available)
    const start = this.start>0 ? (parseInt(this.ets) || parseInt(this.start) || 0) : 0
    this._play(start)
    this.forwardMethod('resume', start)
  } // resume

  pause() {
    if(this.active_source) {
      if(this.listener) {
        this.listener.publish(this.source, 'mmStop')
        this.listener.playing = false
      }
      this.state = 'paused'
    }
    this.forwardMethod('pause')
  } // pause

  toggle() {
    if(this.state === 'paused') {
      this.resume()
    }else {
      this.pause()
    }
  } // toggle

  async _play(start) {
    if(start < 0 || (start > 0 && start < MIN_GOOD_US)) {
      this.error('Invalid start time: '+start+' ('+new Date(start/1000).toISOString()+')')
      start = 0
    }
    if(Date.now()*1_000 - 5*1_000_000 < start) {
      // go live if desired time is closer than 5 seconds to live time
      start = 0
    }
    if(!this.listener) this.listener_idChanged(this.listener_id)
    this.state = 'connecting'
    this.bts = null
    this.bdt = null
    this.active_source = this.source
    let filter
    try{ filter = JSON.parse(this.filter) }catch(e) { filter = this.filter }
    if(typeof (filter)==='string') filter = [['INCLUDE', filter]]
    const selected = parseInt(this.selected) || 0
    if(selected) {
      // keep:
      // all for selected channel
      // I without data for non-selected channel < 128
      // nothing
      filter.push(['SKIP', 'action:mmH264', '.channel>127']) // skip preview channels
      filter.push(['SKIP', 'action:mmH264', '.channel!'+selected, '.nal_type!I']) // keep only I frames for non-selected channels
      filter.push(['DELETE', 'action:mmH264', '.channel!'+selected, '.payload']) // and remove payload from these I frames, we need them only to know that camera is alive
    }else{
      filter.push(['SKIP', 'action:mmH264', '.channel<128'])
    }

    if(this.mock) {
      this.start = start>0 ? start : this.ets
      this.live = true
      this.info(`PLAY MOCKUP ${this.source} STARTING ${start}`)
      this.listener.publish(this.source, 'mmPlay', { start: 0, filter })
      if(this.start) {
        this.listener.publish(this.source, 'mmStartMock', {
          mock_h264_shift: this.mock_h264_shift,
          start: this.start,
          filter: this.mock_filter,
          keyframe_channel: selected,
        })
      }
    }else {
      this.start = start>0 ? start : null
      this.live = !(start>0)
      this.ets = start > 0 ? start : 0
      const params = { start: start>0 ? start : 0, filter }
      if(this.duration) params.duration = this.duration
      if(selected) params.keyframe_channel = selected
      this.info(`PLAY ${this.source} STARTING ${start}`)
      this.listener.publish(this.source, 'mmPlay', params)
    }

    this.listener.playing = true
    this.updateStateLabel()
  } // _play

  async listener_idChanged(value, old) {
    const listener_id = _.ifEmpty(value, 0)
    if(this.listener) {
      if(this.active_source && this.source!==this.active_source && this.listener.playing) {
        this.listener.publish(this.active_source, 'mmStop')
      }
      this.listener.mmExit()
      delete this.listener
    }

    this.listener = new msysListener(listener_id)
    this.listener.subscribe('#WM')
    this.listener.mmPeeringConnected = this.mmPeeringConnected.bind(this)
    this.listener.mmPeeringDisconnected = this.mmPeeringDisconnected.bind(this)
    this.listener.mmPlaying = this.mmPlaying.bind(this)
    this.listener.mmMessage = (sender, params)=>this.mmMessage(sender, params)
    this.listener.mmWindowFocused = this.mmWindowFocused.bind(this)
    this.routingChanged(this.getAttribute('routing'))
    this.window_active = this.getModule().window_active
    this.playIfAuto()
  } // listenerChanged

  async startChanged(value, old, context_user_interaction) {
    this.develUpdate()
    if(!context_user_interaction) return
    this.playIfAuto()
  } // startChanged

  async sourceChanged() {
    this.develUpdate()
    this.playIfAuto()
  } // sourceChanged

  async timelineChanged() {
    const channel0 = await this.getChannel(0)
    const timeline = this.find('mele-actor-timeline')
    if(this.timeline && !timeline) {
      await channel0.getActor('timeline')
    }else if(!this.timeline && timeline) {
      await channel0.deleteActor('timeline')
    }
  } // timelineChanged

  updateStateLabel() {
    if(!this.elState) return
    let label
    const value = this.state
    if(value === 'playing') {
      if(this.live) {
        label = 'video.state.live'
      }else {
        const usec = this.ets > 0 ? this.ets : this.start
        if(usec>0) {
          const str_date = _.usec2ld(usec)
          const str_time = _.usec2lt(usec)
          label = `<playdate>${str_date}</playdate><playtime>${str_time}</playtime>`
        }else {
          label = 'video.state.seeking'
        }
      }
    }else {
      label = 'video.state.'+value
    }
    this.elState.label = label
  } // updateStateLabel

  async stateChanged(value) {
    this.updateStateLabel()
    this.forwardMethod('stateChanged', value)
    // this.develUpdate()
  } // stateChanged

  async filterChanged() {
    this.playIfAuto()
  } // filterChanged

  routingChanged(routing_string) {
    // parse custom routing rules
    try{
      this.routingHash = {}
      if(typeof routing_string==='string' && routing_string.length>0) {
        const routing = JSON.parse(routing_string)
        for(const action in routing) {
          if(!Array.isArray(routing[action])) routing[action] = []
          routing[action] = routing[action].filter(type=>typeof type==='string' && type.length>0)
        }
        this.routingHash = routing
      }
    }catch(e) {
      console.error('Invalid routing for mele-player: '+e)
    }
  } // routingChanged

  async forwardMethod(method, ...args) {
    for(const channel in this.channels) {
      const elChannel = await this.getChannel(channel)
      if(typeof elChannel[method]==='function') {
        elChannel[method](...args)
      }
      elChannel.forwardMethod(method, ...args)
    }
  } // forwardMethod

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by Window Manager when top full-screen window on any screen changes. Sends directly to that window and to #WM queue.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id]
  * @param {integer} [params.screen]
  * @returns {Promise}
  */
  mmWindowFocused(sender, params) {
    this.window_active = !('id' in params) || params.id===this.getModule().listener_id
    if(this.window_active) {
      if(this.source && this.autoplay && this.autopause && !this.listener?.playing) {
        this.play()
      }
    }else{
      if(this.autopause && this.listener?.playing) {
        this.pause()
      }
    }
  } // mmWindowFocused

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} [params.url] - URL of connected peering
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the peering
  */
  mmPeeringConnected(sender, params) {
    if(/@/.test(this.source)) this.playIfAuto()
    this.forwardMethod('connected', params)
  } // mmPeeringConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} [params.url] - URL of disconnected peering
  */
  mmPeeringDisconnected(sender, params) {
    if(/@/.test(this.source)) this.state = this.autoplay ? 'connecting' : 'stopped'
    this.forwardMethod('disconnected', params)
  } // mmPeeringConnected

  async getChannel(channel) {
    channel = _.natural(channel)
    let elChannel = this.channels[channel]
    if(!elChannel) {
      elChannel = this.channels[channel] = this.ownerDocument.createElement('mele-channel')
      if(channel > 0) {
        elChannel.onclick = ()=>{ this.selected = channel }
      }
      this.appendChild(elChannel)
      elChannel.ch = channel
      if(channel>0 && channel===_.natural(this.selected)) {
        elChannel.selected = true
      }
    }
    await elChannel._initialized
    return elChannel
  } // getChannel

  mmPlaying() {
    this.forwardMethod('playing')
  } // mmPlaying

  mmMessage(sender, msg) {
    const now = Date.now()
    const unow = now*1000
    if(!msg.action) {
      this.debug('END OF STREAM')
      return
    }
    if(msg.action==='mmH264' && this.config?.mock) {
      this.mock = msg.params && ('orig_dt' in msg.params)
    }
    if(this.state==='connecting') {
      this.state = 'playing'
    }
    if(this.state==='paused') return
    if(msg.action==='mmH264') msg.received_dt = unow
    if(this.verbose>2 || (this.verbose>1 && msg.action!=='mmGps' && (msg.action!=='mmH264' || msg.params.nal_type==='I'))) {
      const event_dt = msg.params.event_dt // time when event occured
      const sent_dt = msg.event_dt // time when encapsulating mmMessage was created
      const received_bytes = msg._meta.bytes
      this.info(
        'CHANNEL', msg.params.channel || 0,
        'MESSAGE', msg.action + (msg.action==='mmH264' ? ' '+msg.params.nal_type.padEnd(3, ' ') : ''),
        'EVENT', (event_dt/1_000_000).toFixed(3), _.usec2t(event_dt),
        'SENT', (sent_dt/1_000_000).toFixed(3), _.usec2t(sent_dt),
        'RECEIVED', (unow/1_000_000).toFixed(3), _.usec2t(unow),
        'TRANSPORT', ((unow-sent_dt)/1000).toFixed(3) + 'ms',
        'BYTES', (received_bytes/1000).toFixed(3),
      )
    }

    if(msg.action==='mmTime') {
      const event_dt = msg.params.event_dt
      this.mts = event_dt
      if(event_dt > this.ets + 500_000) {
        this.ets = msg.params.event_dt
        this.updateStateLabel()
      }
    }
    if(msg.action==='mmRecordMeta') {
      const el = this.find('mele-button[key=recording]')
      if(el) {
        el.label = msg.params.running ? 'video.recording_on' : 'video.recording_off'
        el.disabled = false
        if(this.disable_recording_btn) clearTimeout(this.disable_recording_btn)
        this.disable_recording_btn = setTimeout(()=>{
          el.disabled = true
          el.label = 'video.recording_off'
        }, 2000)
      }
    }

    this.develUpdate()
    this.message(msg)
  } // mmMessage

  createChannelButton(channel) {
    channel = _.natural(channel)
    if(channel===0) return
    const skiped_channels = Array.isArray(this.skiped_channels) ? this.skiped_channels.map(c=>parseInt(c)) : []
    if(skiped_channels.length > 0 && skiped_channels.includes(channel)) return
    if(this.elChannels.find('@'+channel)) return
    const before = [...this.elChannels.children].find(el=>el.key>channel)
    this.elChannels[before?'prependChildren':'appendChildren']({
      tag: 'mele-button',
      type: 'toolbar',
      key: channel,
      iconleft: 'video.camera.'+channel,
      // @TODO: there is a conficlict, two same eventButtonClicked, commenting out the first one
      // eventButtonClicked: this.rewindMark.bind(this),
      eventButtonClicked: ()=>{ this.selected=channel },
      selected: channel===_.natural(this.selected),
    }, before)
  } // createChannelButton

  async message(msg) {
    let channel = msg.params.channel>0 ? msg.params.channel : 0
    if(channel>=128) channel-=128 // special adjustment: data from channel above 128 (used as low-res version) are routed to corresponding normal channel
    this.createChannelButton(channel)
    const elChannel = await this.getChannel(channel)
    await elChannel.message(msg)
  } // message

  async vtsChanged(vts) {
    // this is called by mele-actor-h264 through selected mele-channel whenever media time of video changes
    const ets = vts
    this.ets = ets
    this.dispatchEvent(new CustomEvent('vts', { detail: vts }))
  } // vtsChanged

  async etsChanged(ets) {
    // this is called by mele-actor-h264 through selected mele-channel whenever media time of video changes
    this.edt = _.usec2t(ets)
    let bts = this.bts
    if(!bts) {
      bts = this.bts = ets
      this.bdt = _.usec2t(bts)
    }
    for(const channel in this.channels) {
      const elChannel = await this.getChannel(channel)
      elChannel.ets = ets
    }
    this.updateStateLabel()
    this.dispatchEvent(new CustomEvent('ets', { detail: ets }))
  } // etsChanged

  async videosizeChanged(new_value) {
    (await this.getChannel(0)).videosize = new_value
    this.dispatchEvent(new CustomEvent('videosize', { detail: new_value }))
  } // videosizeChanged

  async rendersizeChanged(new_value) {
    (await this.getChannel(0)).rendersize = new_value
  } // rendersizeChanged

  async selectedChanged(new_value) {
    const start = this.start>0 ? (parseInt(this.ets) || parseInt(this.start) || 0) : 0

    if(!(new_value>=0)) {
      this.selected = 0
      return
    }
    new_value = _.natural(new_value)
    for(const el of [...this.elChannels.children]) {
      el.selected = new_value === _.natural(el.key)
    }
    this.vts = null
    this.videosize = null
    this.rendersize = null
    for(let channel in this.channels) {
      channel = _.natural(channel)
      const elChannel = await this.getChannel(channel)
      elChannel.selected = channel>0 && new_value===channel
    }

    this.updateDelictButtonVisibility()
    this.playIfAuto(start)
  } // selectedChanged

  develChanged(devel) {
    if(devel) {
      this.elDevel = this.appendChildren({ tag: 'devel' })
    }else{
      this.elDevel.deleteElement()
      delete this.elDevel
    }
  } // develChanged

  develUpdate() {
    // update development information
    if(!this.elDevel) return
    const elVideo = this.querySelector('mele-channel[selected] video')
    const currentTime = elVideo ? Math.round(elVideo.currentTime*1000) : ''
    let buffer_start = 0; let buffer_end = 0
    if(elVideo) {
      for(let i=0; i<elVideo.buffered.length; i++) {
        const end = elVideo.buffered.end(i)
        if(buffer_end<end) {
          buffer_start = Math.round(elVideo.buffered.start(i))
          buffer_end = end
        }
      }
    }
    this.elDevel.innerHTML = `
    src:   ${this.source}
    start: ${this.start > 0 ? (this.start/1_000_000).toFixed(3) : 'LIVE'}
    state: ${String(this.state||'').padStart(14, ' ')} ${NAL.msec2time(Date.now())}
    bts:   ${(this.bts/1_000_000).toFixed(3)} ${this.bdt}
    ets:   ${(this.ets/1_000_000).toFixed(3)} ${this.edt}
    vts:   ${(this.vts/1_000_000).toFixed(3)} ${_.usec2t(this.vts)}
    mts:   ${(this.mts/1_000_000).toFixed(3)} ${_.usec2t(this.mts)}
    buf:   ${elVideo ? (buffer_start+'-'+buffer_end.toFixed(3)).padStart(14, ' ') : ''}
    curr:  ${currentTime>0 ? (currentTime/1000).toFixed(3).padStart(14, ' ') : ''} ${currentTime>0 ? NAL.msec2duration(currentTime).padStart(12, ' ') : ''}
    pts:   ${(this.pts/1_000_000).toFixed(3).padStart(14, ' ')} ${_.usec2duration(this.pts).padStart(12, ' ')}
    frame: ${elVideo ? String(elVideo.last_frame_id||'').padStart(14, ' ') : ''}
  `.replace(/^\s+/mg, '')
  } // develUpdate

  showSeekTimes() {
    const el = this.find('@seek')
    el.selectFirst()
    el.hidden = false
  } // showSeekTimes

  controlsChanged(controls) {
    if(controls && !this.elControls) {
      const btn = { tag: 'mele-button', type: 'toolbar' }
      const single_click = { interclick: 0, interlong: 0 }
      const multi_click = { interclick: 300, interlong: 0 }
      const multi_long_click = { interclick: 300, interlong: 1000 }
      this.elControls = this.appendChildren({
        tag: 'controls',
        children: [
          {
            tag: 'div',
            class: 'left',
            children: [
              { ...btn, ...single_click, key: 'markers', iconleft: 'video.mark', eventButtonClicked: this.openMarkers.bind(this) },
              { ...btn, ...single_click, key: 'calendar', iconleft: 'u.calendar', eventButtonClicked: this.openCalendar.bind(this) },
              { ...btn, ...single_click, key: 'screenshot', iconleft: 'server.download', eventButtonClicked: this.screenshot.bind(this) },
            ],
          },
          {
            tag: 'div',
            class: 'center',
            children: [
              { ...btn, ...multi_long_click, key: 'rwdmark', iconleft: 'video.rwdmark.all', eventButtonClicked: this.rewindMark.bind(this) },
              { ...btn, ...multi_click, key: 'rwd', iconleft: 'video.rwd', eventButtonClicked: this.rwdClick.bind(this), eventButtonDown: this.rwdDown.bind(this) },
              { ...btn, ...single_click, key: 'pause', iconleft: 'video.pause', eventButtonClicked: this.pause.bind(this) },
              { ...btn, ...single_click, key: 'resume', iconleft: 'video.resume', eventButtonClicked: this.resume.bind(this) },
              { ...btn, ...multi_click, key: 'fwd', iconleft: 'video.fwd', eventButtonClicked: this.fwdClick.bind(this), eventButtonDown: this.fwdDown.bind(this) },
              { ...btn, ...multi_long_click, key: 'fwdmark', iconleft: 'video.fwdmark.all', eventButtonClicked: this.forwardMark.bind(this) },
            ],
          },
          {
            tag: 'div',
            class: 'right',
            children: [
              { ...btn, ...single_click, key: 'mock', label: 'Mock', eventButtonClicked: this.mockClicked.bind(this), hidden: !this.config?.mock },
              { tag: 'mele-text', key: 'state', label: '' },
              { ...btn, ...single_click, key: 'recording', label: 'video.recording_off', eventButtonClicked: this.switchRecording.bind(this), disabled: true },
              { ...btn, ...single_click, key: 'live', iconleft: 'video.live', eventButtonClicked: this.livePlay.bind(this, 0) },
            ],
          },
        ],
      })
      this.elSeekLists = this.appendChildren({ tag: 'seek', hidden: true })
      this.elRwdSeek = this.elSeekLists.appendChildren({ key: 'rwdseek', tag: 'mele-list', blank: false, direction: 'row', prefix: 'video.seek.', auto: true, label: '${prefix}${key}', hidden: true, sort: ['@key+'] })
      this.elFwdSeek = this.elSeekLists.appendChildren({ key: 'fwdseek', tag: 'mele-list', blank: false, direction: 'row', prefix: 'video.seek.', auto: true, label: '${prefix}${key}', hidden: true, sort: ['@key+'] })
      this.elState = this.elControls.find('@state')
      this.elHistoricMarker = this.appendChildren({ ...btn, ...single_click, key: 'marker', iconleft: 'video.mark.mark', eventButtonClicked: this.markerClicked.bind(this) })
      this.elDelictButton = this.appendChildren({ ...btn, ...single_click, key: 'delict', iconleft: 'mmodDelicts', eventButtonClicked: this.delictClicked.bind(this) })
      this.updateDelictButtonVisibility()
    }else if(!controls && this.elControls) {
      delete this.elState
      delete this.elRwdSeek
      delete this.elFwdSeek
      this.elControls.deleteElement()
      this.elSeekLists.deleteElement()
      delete this.elControls
      delete this.elSeekLists
      this.elHistoricMarker.deleteElement()
      delete this.elHistoricMarker
      this.elDelictButton.deleteElement()
      delete this.elDelictButton
      this.clearDelictFrames()
      if(this.elDelictTray) {
        this.elDelictTray.deleteElement()
        delete this.elDelictTray
      }
    }
  } // controlsChanged

  screenshot() {
    const elH264 = this.find('mele-channel[selected] mele-actor-h264')
    if(!elH264) return
    const elVideo = elH264.querySelector('video')
    if(!elVideo) return
    const [width, height] = String(elH264.videosize??'').split('x').map(Number)
    if(isNaN(width) || isNaN(height)) return
    const ets = parseFloat(elH264.ets) // event timestamp in micoseconds
    if(isNaN(ets)) return

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if(!ctx) return
    ctx.drawImage(elVideo, 0, 0)

    const dataURL = canvas.toDataURL('image/jpeg', this.screenshot_quality / 100)
    const a = document.createElement('a')
    a.href = dataURL

    const app_id = this.getModule()?.app_id ?? 'unknown'
    const dt = new Date(ets / 1000)
    const yyyy = dt.getUTCFullYear().toString().padStart(4, '0')
    const mm = (dt.getUTCMonth() + 1).toString().padStart(2, '0') // month is 0-based
    const dd = dt.getUTCDate().toString().padStart(2, '0')
    const hh = dt.getUTCHours().toString().padStart(2, '0')
    const mi = dt.getUTCMinutes().toString().padStart(2, '0')
    const ss = dt.getUTCSeconds().toString().padStart(2, '0')
    const ms = dt.getUTCMilliseconds().toString().padStart(3, '0')
    const us = (ets % 1000000).toString().padStart(6, '0')
    const values = {
      ...this.meta,
      app_id,
      appid: app_id,
      yyyy,
      mm,
      dd,
      hh,
      mi,
      ss,
      ms,
      us,
      ets,
      width,
      w: width,
      height,
      h: height,
    }

    a.download = this.screenshot_filename.replace(/\$\{([a-zA-Z0-9_]+)\}/g, (_, key) => String(values[key] ?? '').replace(/[^a-zA-Z0-9_.-]/g, '_'))
    a.click()
  } // screenshot

  markerClicked(start) {
    const marker = this.marker
    if(marker === 'moment') {
      this.momentMarkerClicked(start)
    }else if(marker === 'interval') {
      this.intervalMarkerClicked(start)
    }
  } // markerClicked

  intervalMarkerClicked(usec) {
    if(!this.listener) return
    usec = parseInt(usec)
    if(!(usec>0)) usec = parseInt(this.ets)
    if(!(usec>0)) return

    if(this.marker_turn_off) {
      clearTimeout(this.marker_turn_off)
      delete this.marker_turn_off
    }

    if(this.markering) {
      // if already marking, stop it
      const start = parseInt(this.markering_start)
      const end = usec + (parseInt(this.marker_after) || 0)
      const channel = this.markering_channel
      delete this.markering_start
      delete this.markering_channel
      this.markering = false
      if(start>0 && end>0 && start<end && this.listener && this.active_source) {
        const params = {
          start,
          end,
          type: 'marker',
        }
        if(channel>0) params.channel = channel
        this.listener.publish(this.active_source, 'mmSaveMark', params)
      }
    }else {
      // start marking
      this.markering_start = usec - (parseInt(this.marker_before) || 0)
      this.markering_channel = _.natural(this.selected)
      this.markering = true
      if(this.marker_max>0) {
        this.marker_turn_off = setTimeout(()=>{
          delete this.marker_turn_off
          this.intervalMarkerClicked()
        }, this.marker_max/1000)
      }
    }
  } // intervalMarkerClicked

  momentMarkerClicked(start) {
    if(!this.listener) return
    start = parseInt(start)
    if(!(start>0)) start = parseInt(this.ets) + (parseInt(this.marker_before) || 0)
    if(!(start>0)) return

    const modal = this.modalOpen({
      key: 'mele_player_marker',
      position: 'center',
      outclick: ':modal.cancel',
      header: 'video.create_mark',
      items: [
        { key: 'images', tag: 'div' },
        { key: 'start', label: 'Mark time', tag: 'mele-input', type: 'datetime_us', value: start },
        { key: 'type', label: 'Mark type', tag: 'mele-input', type: 'grid', prefix: 'video.mark.', itemicon: 'icon:${prefix}${key}', auto: true, value: 'mark' },
        { key: 'channel', tag: 'mele-input', type: 'empty', value: this.selected || 0 },
        { key: 'label', label: 'Mark note', tag: 'mele-input', type: 'string' },
      ],
      footerbuttons: ['cancel', { color: 'green', label: 'form.save', action: 'submitvalid' }],
    })

    this.fillWithFrameSnapshots(modal.find('@images'), start, this.selected)

    modal.eventSubmit = ()=>{
      const { start, type, channel, label } = modal.getValues()
      const p = { start, channel, type, label }
      if(!(channel>0)) delete p.channel
      if(String(label??'').length===0) delete p.label
      this.listener.publish(this.active_source, 'mmSaveMark', p)
      modal.deleteElement()
    }

    setTimeout(()=>{
      modal.find('mele-input[key=label]').inputFocus()
    }, 100)
  } // momentMarkerClicked

  async fillWithFrameSnapshots(div, end, channel) {
    const marker_window = (_.natural(this.marker_window) || 1) * 1_000_000
    div.innerHTML = ''
    if(div.observer) {
      div.observer.disconnect()
      div.observer = null
    }
    channel = _.natural(channel)
    if(channel === 0) return
    try{
      const start = end - marker_window
      const request = { channel, start, end, keyframes: false }
      const { params: { list } } = await this.listener.request(this.source, 'mmGetFrameTimes', request, 'mmFrameTimes')
      while(list.length>0 && list[0]<0) list.shift()
      if(list.length===0) return

      const loaded_images = {}
      const handleImageVisibility = async (img) => {
        const start = parseInt(img.getAttribute('start'))
        if(loaded_images[start]) return
        loaded_images[start] = true
        const end = parseInt(img.getAttribute('end'))
        const frames = div.findAll(`img[start="${start}"]`)
        for(const img of frames) {
          div.observer.unobserve(img)
          img.setAttribute('state', 'reading')
          setImageSvg(img)
        }

        try{
          // obtain images from mBox
          const request = { channel, start, end, q: this.marker_q }
          if(this.marker_width>0) request.width = this.marker_width
          if(this.marker_height>0) request.height = this.marker_height
          const { params: { jpegs } } = await this.listener.request(this.source, 'mmGetJPEGs', request, 'mmJPEGs')

          // fill image placeholders with JPEG data
          for(const jpeg of jpegs) {
            const img = div.find(`img[usec="${jpeg.dt}"]`)
            const blob = new Blob([jpeg.payload], { type: 'image/jpeg' })
            const url = URL.createObjectURL(blob)
            img.onerror = () => {
              URL.revokeObjectURL(url)
              img.setAttribute('state', 'error')
              setImageSvg(img)
            }
            img.onload = () => {
              URL.revokeObjectURL(url)
              img.setAttribute('state', 'loaded')
            }
            img.setAttribute('state', 'loading')
            img.src = url
          }
        }catch(e) {
          this.listener.error('Failed to obtain JPEGs for marker preview: '+e)
        }

        // mark images that were not loaded with error
        for(const img of frames) {
          const state = img.getAttribute('state')
          if(state==='reading') {
            img.setAttribute('state', 'error')
            setImageSvg(img)
          }
        }
      } // handleImageVisibility

      div.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if(entry.isIntersecting) {
            handleImageVisibility(entry.target)
          }
        })
      }, {
        root: null, // use viewport as the root
        rootMargin: '0px',
        threshold: 0.10, // 10% of the image is visible
      })

      function setImageSvg(img) {
        const usec = parseInt(img.getAttribute('usec'))
        if(!usec) return
        const date = new Date(usec / 1000)
        const day = String(date.getUTCDate()).padStart(2, '0')
        const month = String(date.getUTCMonth() + 1).padStart(2, '0')
        const year = date.getUTCFullYear()
        const hours = String(date.getUTCHours()).padStart(2, '0')
        const minutes = String(date.getUTCMinutes()).padStart(2, '0')
        const seconds = String(date.getUTCSeconds()).padStart(2, '0')
        const microseconds = String(usec % 1000000).padStart(6, '0')

        const state = img.getAttribute('state')
        let extra = ''
        if(state === 'reading') {
          extra = `
            <g class="reading" transform="scale(0.2) translate(80 100)">
              <path fill="black" d="M20.201,5.169c-8.254,0-14.946,6.692-14.946,14.946c0,8.255,6.692,14.946,14.946,14.946
                s14.946-6.691,14.946-14.946C35.146,11.861,28.455,5.169,20.201,5.169z M20.201,31.749c-6.425,0-11.634-5.208-11.634-11.634
                c0-6.425,5.209-11.634,11.634-11.634c6.425,0,11.633,5.209,11.633,11.634C31.834,26.541,26.626,31.749,20.201,31.749z" />
              <path fill="white" d="M26.013,10.047l1.654-2.866c-2.198-1.272-4.743-2.012-7.466-2.012h0v3.312h0 C22.32,8.481,24.301,9.057,26.013,10.047z" transform="rotate(48 20 20)">
                  <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="0.7s" repeatCount="indefinite" />
              </path>
            </g>
            `
        }else if(state === 'error') {
          extra = `
            <g class="error" transform="scale(0.2) translate(80 100)">
              <path stroke="hsl(355, 67%, 40%)" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" d="M8,8 l24,24 m0,-24 l-24,24"></path>
            </g>
            `
        }

        img.src = `data:image/svg+xml;utf8,
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 30">
          <rect width="100%" height="100%" fill="lightgray" />
          <text x='50%' y='35%' font-size='5' fill='black' dominant-baseline='middle' text-anchor='middle'>${day}.${month}.${year}</text>
          <text x='50%' y='55%' font-size='5' fill='black' dominant-baseline='middle' text-anchor='middle'>${hours}:${minutes}:${seconds}.${microseconds}</text>
          ${extra}
        </svg>`
      } // setImageSvg

      // construct images with timestamps
      let start_us = 0
      let end_us = 0
      for(let i=0; i<list.length; i++) {
        const usec = Math.abs(list[i])
        const is_keyframe = list[i] > 0
        if(is_keyframe) {
          start_us = usec
          const next_keyframe = list.findIndex((val, index) => val > 0 && index > i)
          end_us = Math.abs(list[next_keyframe < 0 ? (list.length-1) : (next_keyframe-1)])
        }

        const img = document.createElement('img')
        if(is_keyframe) img.setAttribute('keyframe', '')
        img.setAttribute('start', start_us)
        img.setAttribute('end', end_us)
        img.setAttribute('usec', usec)
        setImageSvg(img)

        div.insertBefore(img, div.firstChild)
        div.observer.observe(img)

        img.onclick = () => {
          const divRect = div.getBoundingClientRect()
          const imgRect = img.getBoundingClientRect()
          const divCenterX = divRect.width / 2
          const imgCenterX = imgRect.width / 2
          const scrollToX = img.offsetLeft - divCenterX + imgCenterX
          div.scrollTo({
            left: scrollToX,
            behavior: 'smooth', // This provides smooth scrolling
          })
        }
      }

      const images = div.querySelectorAll('img')

      function detectCenteredImage() {
        const divRect = div.getBoundingClientRect()
        const divCenterX = divRect.left + divRect.width / 2
        let closestImage = null
        let closestDistance = Infinity
        images.forEach(img => {
          const imgRect = img.getBoundingClientRect()
          const imgCenterX = imgRect.left + imgRect.width / 2
          const distanceToCenter = Math.abs(imgCenterX - divCenterX)
          if (distanceToCenter < closestDistance) {
            closestDistance = distanceToCenter
            closestImage = img
          }
        })
        return closestImage
      } // detectCenteredImage

      // Function to center the specified image in the div
      function scrollToCenterImage(img) {
        const divRect = div.getBoundingClientRect()
        const imgRect = img.getBoundingClientRect()
        const divCenterX = divRect.width / 2
        const imgCenterX = imgRect.width / 2
        const scrollPosition = img.offsetLeft - (divCenterX - imgCenterX)
        div.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
          // behavior: 'instant',
        })
      } // scrollToCenterImage

      let fixer = null
      div.onscroll = () => {
        const img = detectCenteredImage()
        if(!img) return
        const selected = div.find('img[selected]')
        if(selected && selected!==img) selected.removeAttribute('selected')
        if(!img) return
        if(selected!==img) img.setAttribute('selected', '')
        const usec = parseInt(img.getAttribute('usec'))
        const elTime = div.closest('.mele-container').find('@start')
        if(!elTime) return
        const current = parseInt(elTime.value)
        if(usec !== current) elTime.value = usec
        if(fixer) clearTimeout(fixer)
        fixer = setTimeout(()=>{
          fixer = null
          const img = detectCenteredImage()
          if(img) scrollToCenterImage(img)
        }, 100)
      } // div.onscroll

      setTimeout(()=>{
        const img = images[0]
        if(!img) return
        scrollToCenterImage(img)
        setTimeout(()=>{
          div.onscroll()
        }, 100)
      }, 100)
    }catch(e) {
      this.error(e)
    }
  } // fillWithFrameSnapshots

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Handle click on the delict button in historic playback.
   * Opens the collection tray and adds the currently displayed frame to it.
   * Requested playback time is snapped to the exact time of the nearest H264 frame,
   * so that the delict wizard can later retrieve the very same frames.
   */
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Returns true if delicts can be created on given channel according to delict_channels;
   * empty delict_channels means all channels are allowed.
   */
  isDelictChannel(channel) {
    const allowed = this.delict_channels
    if(!Array.isArray(allowed) || allowed.length===0) return true
    return allowed.includes(_.natural(channel))
  } // isDelictChannel

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Shows/hides the delict button according to the currently selected channel.
   */
  updateDelictButtonVisibility() {
    if(!this.elDelictButton) return
    this.elDelictButton.hidden = !this.isDelictChannel(this.selected)
  } // updateDelictButtonVisibility

  async delictClicked() {
    if(!this.listener) return
    const channel = _.natural(this.selected)
    const ets = parseInt(this.ets)
    if(!this.isDelictChannel(channel)) return
    this.openDelictTray()
    if(!(ets>0) || !(channel>0)) return

    if(!this.delict_frames) this.delict_frames = []

    // all frames of one delict must come from the same channel
    if(this.delict_frames.length>0 && this.delict_frames[0].channel!==channel) {
      this.blinkDelictTray()
      return
    }

    const max = _.natural(this.delict_max) || 8
    if(this.delict_frames.length>=max) {
      this.blinkDelictTray()
      return
    }

    try{
      const request = { channel, dt: ets, q: this.marker_q }
      if(this.marker_width>0) request.max_width = this.marker_width
      if(this.marker_height>0) request.max_height = this.marker_height
      const reply = await this.listener.request(this.source, 'mmGetJPEG', request, 'mmJPEG')
      const { dt, payload } = reply?.params || {}
      if(!(dt>0) || !payload) throw new Error('frame image not found')
      if(!this.delict_frames.some(frame=>frame.dt===dt)) {
        const blob = new Blob([payload], { type: 'image/jpeg' })
        this.delict_frames.push({ dt, channel, url: URL.createObjectURL(blob) })
        this.delict_frames.sort((a, b)=>a.dt-b.dt)
      }
    }catch(e) {
      this.listener.error('Failed to obtain frame for delict: '+e)
    }
    this.refreshDelictTray()
  } // delictClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Creates (if needed) and shows the tray with frames collected for delict creation.
   */
  openDelictTray() {
    if(!this.elDelictTray) {
      this.elDelictTray = this.appendChildren({
        tag: 'delicttray',
        children: [
          {
            tag: 'div',
            class: 'head',
            children: [
              { tag: 'mele-text', class: 'tray_title', label: 'video.delict.title' },
              { tag: 'mele-text', class: 'tray_hint', label: 'video.delict.hint' },
              { tag: 'button', key: 'close', class: 'close' },
            ],
          },
          {
            tag: 'div',
            class: 'row',
            children: [
              { tag: 'div', key: 'frames' },
              { tag: 'mele-button', key: 'create', type: 'action', color: 'orange', label: 'video.delict.create', eventButtonClicked: this.delictCreateClicked.bind(this) },
            ],
          },
        ],
      })
      const elClose = this.elDelictTray.find('button[key=close]')
      elClose.textContent = '×'
      elClose.onclick = this.closeDelictTray.bind(this)
      // remove blink class after animation so that adding it again restarts the animation
      this.elDelictTray.addEventListener('animationend', () => {
        this.elDelictTray?.classList.remove('blink')
      })
      this.refreshDelictTray()
    }
    this.elDelictTray.hidden = false
    this.updateDelictButtonState()
  } // openDelictTray

  closeDelictTray() {
    if(this.elDelictTray) this.elDelictTray.hidden = true
    this.updateDelictButtonState()
  } // closeDelictTray

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Highlights the delict button while the tray is open or frames are collected,
   * so the user knows where to tap to add further frames.
   */
  updateDelictButtonState() {
    if(!this.elDelictButton) return
    const tray_open = !!this.elDelictTray && !this.elDelictTray.hidden
    const collecting = (this.delict_frames?.length || 0) > 0
    this.elDelictButton.classList.toggle('collecting', tray_open || collecting)
  } // updateDelictButtonState

  blinkDelictTray() {
    if(!this.elDelictTray) return
    this.elDelictTray.classList.add('blink')
  } // blinkDelictTray

  clearDelictFrames() {
    for(const frame of this.delict_frames || []) {
      if(frame.url) URL.revokeObjectURL(frame.url)
    }
    this.delict_frames = []
  } // clearDelictFrames

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Redraws thumbnails of collected frames in the delict tray
   * and updates the state of the create button.
   */
  refreshDelictTray() {
    if(!this.elDelictTray) return
    const frames = this.delict_frames || []
    const elFrames = this.elDelictTray.find('[key=frames]')
    const elCreate = this.elDelictTray.find('[key=create]')
    elFrames.innerHTML = ''
    for(const frame of frames) {
      const { dt, channel, url } = frame
      const item = document.createElement('item')
      item.setAttribute('dt', dt)
      item.setAttribute('channel', channel)
      const [date_str, time_str] = _.debugdatetime(Math.floor(dt/1000)).split(' ')
      item.innerHTML = `<datetime><date>${date_str}</date><time>${time_str}</time></datetime>`
      const img = document.createElement('img')
      img.src = url
      item.appendChild(img)
      const remove = document.createElement('button')
      remove.className = 'remove'
      remove.textContent = '×'
      remove.onclick = (e)=>{
        e.stopPropagation()
        if(frame.url) URL.revokeObjectURL(frame.url)
        this.delict_frames = this.delict_frames.filter(f=>f.dt!==dt)
        this.refreshDelictTray()
      }
      item.appendChild(remove)
      elFrames.appendChild(item)
    }
    elCreate.disabled = frames.length===0
    const count = frames.length
    const plural = count===1 ? 'one' : count<5 ? 'few' : 'many'
    elCreate.label2 = count>0 ? count+' '+this.translate('video.delict.photo.'+plural) : ''
    this.updateDelictButtonState()
  } // refreshDelictTray

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Handle click on the create button in the delict tray.
   * Hands the collected frames over via eventDelictCreate and resets the tray.
   */
  delictCreateClicked() {
    const frames = this.delict_frames || []
    if(frames.length===0) return
    const params = {
      channel: frames[0].channel,
      event_dt: frames[0].dt,
      jpegs: frames.map(frame=>({ event_dt: frame.dt })),
    }
    this.clearDelictFrames()
    this.refreshDelictTray()
    this.closeDelictTray()
    if(typeof this.eventDelictCreate==='function') {
      this.eventDelictCreate(params)
    }
  } // delictCreateClicked

  mockClicked() {
    if(this.mock) {
      const start = this.ets || this.start || 0
      this.listener.publish(this.source, 'mmStopMock')
      if(this.listener.playing) {
        this.play(start)
      }
    }else {
      const mock_h264_shift = parseInt(this.mock_h264_shift) || 0
      const start = this.ets || this.start || 0
      const filter = this.mock_filter
      const params = { mock_h264_shift, filter, start }
      const selected = parseInt(this.selected)
      if(selected>0) {
        params.keyframe_channel = selected
      }
      if(start) {
        this.listener.publish(this.source, 'mmStartMock', params)
      }
      if(this.listener.playing) {
        this.play(0)
      }
    }
  } // mockClicked

  switchRecording() {
    if(!this.listener) return
    if(this.recording!=='enabled') return
    const el = this.find('mele-button[key=recording]')
    if(!el) return
    el.disabled = true
    if(el.label==='video.recording_on') {
      this.listener.publish(this.source, 'mmRecordStop')
    }else {
      const modal = this.modalOpen({
        key: 'recording_start_modal',
        position: 'center',
        outclick: ':modal.cancel',
        header: 'video.start_recording',
        items: [
          { key: 'label', label: 'Note', tag: 'mele-input', type: 'string' },
        ],
        footerbuttons: [
          {
            key: 'cancel',
            color: 'red',
            label: 'form.cancel',
            eventButtonClicked: ()=>{
              modal.deleteElement()
            },
          },
          {
            key: 'ok',
            color: 'green',
            label: 'form.ok',
            eventButtonClicked: ()=>{
              let { label } = modal.getValues()
              label = String(label??'')
              this.listener.publish(this.active_source, 'mmRecordStart', { note: label })
              modal.deleteElement()
            },
          },
        ],
      })
    }
  } // switchRecording

  async getMounts() {
    if(!this.listener) return []
    let re = /.*/
    try{
      if(this.mounts) {
        re = new RegExp(this.mounts)
      }
    }catch(e) {
      console.error('Invalid mounts attribute: '+this.mounts)
      return
    }
    let non = /^$/
    try{
      if(this.mounts_exclude) {
        non = new RegExp(this.mounts_exclude)
      }
    }catch(e) {
      console.error('Invalid mounts_exclude attribute: '+this.mounts_exclude)
    }
    try{
      const { params: { fs } } = await this.listener.request('SERVER@msysDM', 'mmGetSysInfo', { keys: ['fsSize'] }, 'mmSysInfo')
      const mounts = fs.map(disk=>disk.mount).filter(mount=>re.test(mount) && !non.test(mount))
      const extras = Array.isArray(this.mounts_extra) ? this.mounts_extra : []
      for(const extra of extras) {
        if(typeof extra==='string' && extra!=='' && !mounts.includes(extra)) mounts.push(extra)
      }
      if(mounts.length>0) return mounts
    }catch(e) {
      console.error(e)
    }
  } // getMounts

  async openMarkers() {
    const elPopup = this.popupOpen({
      key: 'mele_player_markers',
      class: 'popupdialog',
      header: '',
      position: 'center',
      outclick: ':modal.cancel',
      items: [{
        tag: 'mele-list',
        key: 'mark_list',
      }],
    })

    if(!elPopup.isConnected) return // we were removed from DOM

    const sec = Math.floor((parseInt(this.ets)/1_000_000) || (Date.now()/1_000))
    const start_us = (sec - this.markers_before) * 1_000_000
    const end_us = (sec + this.markers_after) * 1_000_000
    let messages = []

    const markers = await this.listener.getMessages(start_us, end_us, [['KEEP', 'action=mmMark']], 'mark')
    messages = messages.concat(markers)
    if(!elPopup.isConnected) return // we were removed from DOM

    const calibrations = await this.listener.getMessages(start_us, end_us, [['KEEP', 'action=mmDopplerCalibration']], 'dopplercalibration')
    messages = messages.concat(calibrations)
    if(!elPopup.isConnected) return // we were removed from DOM

    messages = messages.sort((a, b)=>b.params.event_dt-a.params.event_dt)

    const items = messages.map(({ action, params })=>{
      if(action==='mmDopplerCalibration') {
        const { calibration_dt, remark } = params.calibration || {}
        if(!calibration_dt) return null
        return {
          key: calibration_dt+':calibration',
          class: 'calibration',
          color: 'blue',
          iconleft: 'mmodDoppler',
          label: remark,
          label2: _.usec2ymdhmsus(calibration_dt),
        }
      }else if(action==='mmMark') {
        const { event_dt, type, label } = params
        return {
          key: event_dt+':'+String(type??''),
          class: 'mark',
          iconleft: type==='text' ? 'video.textmark' : 'video.mark',
          iconright: 'sys.close',
          color: type==='text' ? '' : type==='orange' || type==='warn' ? 'orange' : type==='red' || type==='alert' ? 'red' : 'green',
          label: label ?? '-',
          label2: _.usec2ymdhmsus(event_dt),
          data: params,
        }
      }else {
        return null
      }
    }).filter(x=>!!x)

    const elList = elPopup.find('@mark_list')
    elList.items = items
    elList.itemClicked = (item, icon)=>{
      if(icon==='right') {
        const m = /^(\d+):(\w+)/.exec(item.key)
        if(!m) return
        const [, start, type] = m
        const label = item.label==='-' ? '' : item.label
        this.modalConfirm('Are you sure?', ()=>{
          item.deleteElement()
          this.listener.publish(this.source, 'mmDeleteMark', { start, end: start, type, label })
        })
      }else {
        const start_us = parseInt(String(item.key??'').replace(/:.*/, ''))
        if(start_us > MIN_GOOD_US) {
          this.play(start_us)
        }
        this.popupClose('mele_player_markers')
      }
    }

    // initate loading of preview images
    this.loadMarkerPreviewImages(elList)
  } // openMarkers

  async loadMarkerPreviewImages(elList) {
    const marker_items = elList.findAll('mele-item.mark')
    for(const item of marker_items) {
      if(!elList.isConnected) return // we were removed from DOM
      if(!item.isConnected) continue // item was removed from DOM
      await this.loadMarkerPreviewImage(item)
    }
  } // loadMarkerPreviewImages

  async loadMarkerPreviewImage(item) {
    let { event_dt, channel } = item.data || {}
    event_dt = parseInt(event_dt)
    channel = parseInt(channel)
    if(isNaN(channel)) channel = 1
    if(!(event_dt>0 && channel>0)) return
    try{
      const request = {
        channel,
        dt: event_dt,
        around: 50_000, // 50 milliseconds
        q: this.marker_q,
        max_width: 128,
        max_height: 72,
      }
      const reply = await this.listener.request(this.source, 'mmGetJPEG', request, 'mmJPEG')
      if(!item.isConnected) return // we were removed from DOM while getting JPEG
      const payload = reply.params?.payload
      if(!payload) return // no JPEG data received

      const icon = item.find('mele-icon.left')
      if(!icon) return // no icon found in item
      const blob = new Blob([payload], { type: 'image/jpeg' })
      const url = URL.createObjectURL(blob)
      const img = new Image()
      try {
        await new Promise((resolve, reject) => {
          img.onload = () => resolve(img)
          img.onerror = () => reject(new Error())
          img.src = url
        })
      }catch(e) {
        URL.revokeObjectURL(url)
        return
      }
      URL.revokeObjectURL(url)
      if(!icon.isConnected) return // we were removed from DOM
      icon.innerHTML = '' // clear existing icon
      icon.appendChild(img) // append the new image
    }catch(e) {
      // ignore errors
    }
  } // loadMarkerPreviewImage

  async openCalendar() {
    const elModal = this.modalOpen({
      key: 'video_calendar',
      outclick: ':modal.cancel',
      position: 'center',
      header: '{{video.calendar}}',
      items: [
        {
          tag: 'mele-view',
          view: true,
        },
      ],
      footerbuttons: [
        {
          key: 'seek',
          color: 'green',
          label: 'form.seek',
          eventButtonClicked: ()=>{
            elModal.modalClose()
            const us = elStartTime.value * 1_000_000
            if(us > MIN_GOOD_US) {
              this.play(us)
            }
          },
        },
        { key: 'export', color: 'blue', label: '{{form.export}} >>', disabled: true, eventButtonClicked: exportClicked.bind(this) },
      ],
    })

    elModal.setAttribute('subview', 'start')

    elModal.performSeek = (event_dt) => {
      elModal.modalClose()
      const us = parseInt(event_dt)
      if(us > MIN_GOOD_US) {
        this.play(us)
      }
    }
    const options = Object.keys(this.exports)
    const elView = elModal.find('mele-view')
    const [elMain, elType, elDestination] = elView.appendItems([
      {
        tag: 'div',
        class: 'main',
        children: [
          { tag: 'div', class: 'center' },
          { tag: 'div', class: 'right' },
        ],
      },
      { tag: 'mele-input', key: 'type', type: 'grid', label: 'video.export.type', itemlabel: '${prefix}${key}', prefix: 'export.type.', options, hidden: true, multi: true },
      { tag: 'mele-input', key: 'destination', type: 'list', label: 'video.export.destination', itemlabel: '${key}', hidden: true },
    ])
    const [elStartTime, elEndTime, elCalendar] = elMain.find('.center').appendChildren([
      { tag: 'mele-calendar-datetime', label: 'video.export.start' },
      { tag: 'mele-calendar-datetime', label: 'video.export.end', hidden: true },
      { tag: 'mele-calendar-player', listener_id: this.listener_id ? this.listener_id+'_calendar' : 0 },
    ])
    const [elSnapshots] = elMain.find('.right').appendChildren([
      { key: 'snapshots', tag: 'mele-calendar-snapshots', listener_id: this.listener_id ? this.listener_id+'_snapshots' : 0 },
    ])
    elStartTime.calendar = elEndTime.calendar = elCalendar
    elStartTime.snapshots = elEndTime.snapshots = elSnapshots
    elStartTime.eventSelected = elEndTime.eventSelected = (el, selected)=>{
      if(selected) {
        elModal.forAll('mele-calendar-datetime', cal=>{ if(cal!==el) cal.selected='' })
      }
    }
    elStartTime.value = Math.floor((this.ets/1_000_000) || (Date.now()/1000))
    elStartTime.selected = 'minute'
    elCalendar.source = this.source
    elSnapshots.channel = this.selected
    elSnapshots.source = this.source

    const elSeekBtn = elModal.find('@seek')
    const elExportBtn = elModal.find('@export')

    if(await this.getMounts()) {
      elExportBtn.disabled = false
    }

    let export_screen = 0
    async function exportClicked() {
      export_screen++
      elSeekBtn.hidden = true
      elEndTime.hidden = false
      if(export_screen===1) {
        elModal.setAttribute('subview', 'end')
        elStartTime.selected = ''
        elEndTime.value = elStartTime.value
        elEndTime.selected = 'minute'
      }else if(export_screen===2) {
        elModal.setAttribute('subview', 'export')
        elSnapshots.deleteElement()
        const mounts = await this.getMounts()
        if(!mounts) return
        elStartTime.readonly = elEndTime.readonly = true
        elStartTime.calendar = elEndTime.calendar = null
        elStartTime.selected = ''
        elEndTime.selected = ''
        elCalendar.hidden = true
        elType.hidden = elDestination.hidden = false
        elDestination.options = mounts
        elExportBtn.disabled = !elDestination.value || !elType.value
        elType.eventValueChanged = elDestination.eventValueChanged = (_, value)=>{
          elExportBtn.disabled = !elDestination.value || !elType.value
        }
      }else if(export_screen===3) {
        const start_sec = elStartTime.value
        const end_sec = elEndTime.value
        const type = elType.value
        const destination = elDestination.value
        elModal.deleteElement()
        setTimeout(() => {
          this.startExport(start_sec, end_sec, type, destination)
        }, 1)
      }
    }
  } // openCalendar

  async startExport(start_sec, end_sec, type, destination) {
    const types = String(type??'').split('∫').filter(s=>s)
    if(types.length===0) return

    const listener_id = this.listener_id ? this.listener_id + '_export' : 0
    const listener = new msysListener(listener_id)
    const elWait = this.modalWait('Please wait', 'form.export')
    const elWaitText = elWait.find('mele-text')

    let ok = 0
    let total = 0
    for(const type of types) {
      if(/^marked/.test(type)) {
        const result = await this.startExportMarked(listener, elWaitText, start_sec, end_sec, type, destination)
        if(!result) continue
        ok += result.ok
        total += result.total
        if(result.result === 'mmUndelivered') break
      }else {
        const result = await this.startExportType(listener, elWaitText, start_sec, end_sec, type, destination)
        if(!result) continue
        ok += result.ok
        total += result.total
        if(result.result === 'mmUndelivered') break
      }
    }

    listener.mmExit(null, { delete: true })

    const failed = total - ok
    if(total === 0) {
      this.modalError('export.modal.nothing_exported', 'form.export')
    }else if(failed > 0) {
      this.modalError(`${ok} {{export.modal.succeeded}} and ${failed} {{export.modal.failed}}`, 'form.export')
    }else {
      this.modalInfo(`${ok} {{export.modal.succeeded}}`, 'form.export')
    }
  } // startExport

  async startExportType(listener, elWaitText, start_sec, end_sec, type, destination) {
    const exportParams = this.createExportParams(type, destination, start_sec, end_sec)
    if(!exportParams) return
    const index = type === 'radar' ? 'radarhit' : type === 'doppler' ? 'dopplerhit' : 'h264'

    elWaitText.label = `{{export.type.${type}}}: 0 / 0`

    let ok = 0
    let total = 0
    listener.mmProgress = (s, params) => {
      const { nom, den } = params
      ok = nom
      total = den
      elWaitText.label = `{{export.type.${type}}}: ${ok} / ${total}`
    }

    try {
      const params = {
        ref: Date.now(),
        start: start_sec * 1_000_000,
        end: end_sec * 1_000_000,
        delay: 10,
        index,
        ...exportParams,
      }
      const options = {
        timeout: 6000000,
        resolve: 'mmSuccess',
      }
      const reply = await listener.request('SERVER@msysStreamer', 'mmPlay', params, options)
      const { nom, den } = reply?.params
      if(!isNaN(nom) && !isNaN(den)) {
        ok = nom
        total = den
        elWaitText.label = `{{export.type.${type}}}: ${ok} / ${total}`
      }
      return {
        ok,
        total,
        result: reply.action,
        error: reply.action === 'mmSuccess' ? null : (reply.params?.error || reply.params?.text || reply.params?.code || reply.action),
      }
    }catch (e) {
      return {
        ok,
        total,
        result: 'error',
        error: String(e),
      }
    }
  } // startExportType

  createExportParams(type, destination, start_sec, end_sec) {
    const def = this.exports?.[type]
    if(!def || typeof def !== 'object') return null
    if(!(start_sec > 0 && end_sec > start_sec)) return null

    const start = _.yyyymmdd(start_sec * 1000) + '_' + _.hhmmss(start_sec * 1000)
    const end = _.yyyymmdd(end_sec * 1000) + '_' + _.hhmmss(end_sec * 1000)
    const now = _.ymdhms(Date.now())

    const base = `${type}_from_${start}_to_${end}_at_${now}`

    const params = {
      filter: def.filter,
    }

    if(def.type === 'video') {
      params.outmp4 = `${destination}/mosy_export/${base}.mp4`
    }else {
      params.outdir = `${destination}/mosy_export/${base}`
    }

    return params
  } // createExportParams

  async startExportMarked(listener, elWaitText, start_sec, end_sec, type, destination) {
    const def = this.exports?.[type]
    if(!def) return

    let ok = 0
    let total = 0
    elWaitText.label = `{{export.type.${type}}}: 0 / ${total}`

    // firstly find all marks in defined time window
    const start = start_sec * 1_000_000
    const end = end_sec * 1_000_000
    let markers = []
    try{
      const messages = await listener.getMessages(start, end, [['KEEP', 'action=mmMark']], 'mark')
      markers = messages.map(m=>m.params).filter(p=>p.event_dt>=start && p.event_dt<=end)
      total = markers.length
    }catch(e) {
      return {
        ok,
        total,
        result: 'error',
        error: String(e),
      }
    }

    if(markers.length===0) {
      return {
        ok,
        total,
        result: 'mmSuccess',
      }
    }

    // export each mark individually
    elWaitText.label = `{{export.type.${type}}}: 0 / ${total}`
    let processed = 0
    for(const mark of markers) {
      let { event_dt: start, end } = mark
      if(!(start > 0)) continue
      if(!(end >= start)) end = start + 5_000_000
      const start_sec = Math.floor(start/1_000_000)
      const end_sec = Math.floor(end/1_000_000)
      const ref = Date.now()
      const start_ms = start_sec * 1000
      const end_ms = end_sec * 1000
      const start_s = _.yyyymmdd(start_ms) + '_' + _.hhmmss(start_ms)
      const end_s = _.yyyymmdd(end_ms) + '_' + _.hhmmss(end_ms)
      const now_s = _.ymdhms(ref)
      const base = `${type}_from_${start_s}_to_${end_s}_at_${now_s}`

      const params = {
        ref,
        start: start_sec * 1_000_000,
        end: end_sec * 1_000_000 + 999_999,
        delay: 10,
        filter: def.filter,
        outmp4: `${destination}/mosy_export/${base}.mp4`,
      }

      try{
        const reply = await listener.request('SERVER@msysStreamer', 'mmPlay', params, { timeout: 6000000, resolve: 'mmSuccess' })
        const { action } = reply
        if(action === 'mmSuccess') {
          ok++
        }else if(action === 'mmUndelivered') {
          return {
            ok,
            total,
            result: action,
            error: String(reply.params?.error || reply.params?.text || reply.params?.code || reply.action),
          }
        }
        processed++
        elWaitText.label = `{{export.type.${type}}}: ${processed} / ${total}`
      }catch(e) {
        return {
          ok,
          total,
          result: 'error',
          error: String(e?.params?.error || e?.params?.text || e?.params?.code || e?.message || String(e)),
        }
      }
    }

    return {
      ok,
      total,
      result: 'mmSuccess',
    }
  } // startExportMarked

  livePlay() {
    if(typeof (this.eventLive)==='function') {
      try {
        this.eventLive()
      } catch (e) {
        this.error('Error in eventLive handler' + e)
      }
    }
    this.play(0)
  } // livePlay

  async rewindMark() {
    try{
      const ets = parseInt(this.ets)
      if(!(ets > MIN_GOOD_US)) return
      const end = ets - this.search_rwd * 1_000_000 // slightly forward beginning of search
      const start = end - this.search_window * 1_000_000
      const reply = await this.listener.request('SERVER@msysStreamer', 'mmFindMessage', { index: 'mark', start, end, direction: 'backward' }, 'mmMessage')
      const event_dt = reply.params.params.event_dt
      this.play(event_dt)
    }catch(e) {
      // ignore
    }
  } // rewindMark

  async forwardMark() {
    try{
      const ets = parseInt(this.ets)
      if(!(ets > MIN_GOOD_US)) return
      const start = ets + this.search_fwd * 1_000_000 // slightly forward beginning of search
      const end = start + this.search_window * 1_000_000
      const reply = await this.listener.request('SERVER@msysStreamer', 'mmFindMessage', { index: 'mark', start, end, direction: 'forward' }, 'mmMessage')
      const event_dt = reply.params.params.event_dt
      this.play(event_dt)
    }catch(e) {
      // ignore
    }
  } // forwardMark

  rwdDown() {
    if(this.elRwdSeek) {
      this.elSeekLists.hidden = this.elRwdSeek.hidden = false
      this.elRwdSeek.selectNext()
    }
  } // rwdClick

  rwdClick() {
    if(!this.elRwdSeek) return
    const seconds = this.elRwdSeek.selected[0]
    this.elRwdSeek.selected = []
    this.elSeekLists.hidden = this.elRwdSeek.hidden = true
    if(seconds>0) {
      const ts = this.state==='playing' ? (this.ets || this.start || Date.now()*1000) : (this.start || Date.now()*1000)
      let new_ts = ts - seconds*1_000_000
      if(Date.now()*1_000 - 5*1_000_000 < new_ts) {
        // go live if desired time is closer than 5 seconds to live time
        new_ts = 0
      }
      this.play(new_ts)
    }
  } // rwdClick

  fwdDown() {
    if(this.elFwdSeek) {
      this.elSeekLists.hidden = this.elFwdSeek.hidden = false
      this.elFwdSeek.selectNext()
    }
  } // fwdDown

  fwdClick() {
    if(!this.elFwdSeek) return
    const seconds = this.elFwdSeek.selected[0]
    this.elFwdSeek.selected = []
    this.elSeekLists.hidden = this.elFwdSeek.hidden = true
    if(seconds>0) {
      const ts = this.state==='playing' ? (this.ets || this.start || Date.now()*1000) : (this.start || Date.now()*1000)
      let new_ts = ts + seconds*1_000_000
      if(Date.now()*1_000 - 5*1_000_000 < new_ts) {
        // go live if desired time is closer than 5 seconds to live time
        new_ts = 0
      }
      this.play(new_ts)
    }
  } // fwdClick

  autplayChanged() {
    this.info('autplay changed')
  } // autplayChanged
} // class melePlayer

melePlayer._properties = PROPERTIES
melePlayer.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-player', melePlayer)
